import api from '@/redux/api/saveSearch';
import cloneDeep from 'lodash/cloneDeep';
import { createSaveSearchKeyBySearchParams } from './saveSearch';
import { createSelector } from 'reselect';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { getItemSavedSearchQuery } from '@/utils/ItemPageUtils';
import { GlobalState } from '../rootReducer';
import { LOCATION_CHANGE } from 'connected-react-router';

/* Action Types */
export const SAVE_SEARCH_CHECK_FAIL = 'la/ui/saveSearchCheck/FAIL';
export const SAVE_SEARCH_CHECK_REQUEST = 'la/ui/saveSearchCheck/REQUEST';
export const SAVE_SEARCH_CHECK_SUCCESS = 'la/ui/saveSearchCheck/SUCCESS';

// reducer
interface DefaultState {
    readonly byId: {
        [key: string]: {
            isSavedSearch: boolean;
            key: string;
            savedSearchId: number;
            success: boolean;
        };
    };
    error: boolean;
}

const DEFAULT_STATE: DefaultState = {
    byId: {},
    error: false,
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;

    switch (action.type) {
        case LOCATION_CHANGE:
            return DEFAULT_STATE;
        case SAVE_SEARCH_CHECK_FAIL:
            return {
                ...state,
                error: true,
            };
        case SAVE_SEARCH_CHECK_REQUEST:
            return {
                ...state,
                error: false,
            };
        case SAVE_SEARCH_CHECK_SUCCESS:
            existing = cloneDeep(state.byId);

            if (action.payload.saveSearchKey) {
                existing[action.payload.saveSearchKey] = {
                    isSavedSearch: action.payload.savedSearchId > 0,
                    key: action.payload.saveSearchKey,
                    savedSearchId: action.payload.savedSearchId,
                    success: true,
                };
            }

            return {
                ...state,
                byId: existing,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const saveSearchCheckStateSelector = (state: GlobalState) => state.saveSearchCheck;

export const getSaveSearchCheckState: (state: any) => any = createSelector(
    saveSearchCheckStateSelector,
    (saveSearchCheckState) => saveSearchCheckState
);

const idSelector = (state: GlobalState, id) => id;

export const getSaveSearchCheckStateByKey = createSelector(
    [getSaveSearchCheckState, idSelector],
    (saveSearchCheckState, key) => {
        return (
            saveSearchCheckState.byId[key] || {
                isSavedSearch: false,
                key,
                savedSearchId: 0,
                success: true,
            }
        );
    }
);

export const getSavedSearchIdByKey = createSelector([getSaveSearchCheckStateByKey], (stateForKey) => {
    return stateForKey?.savedSearchId || 0;
});

/* ACTION CREATORS */
export const submitIsSavedSearchCheck = (ssParams: any, itemPageFacets?: any) => async (
    dispatch: Function,
    getState: Function
) => {
    try {
        const saveSearchKey = createSaveSearchKeyBySearchParams(ssParams);
        if (itemPageFacets) {
            ssParams = getItemSavedSearchQuery(itemPageFacets);
            if (!ssParams.category_id) {
                return new Promise((resolve) => {
                    resolve(
                        dispatch({
                            payload: { saveSearchKey },
                            type: SAVE_SEARCH_CHECK_SUCCESS,
                        })
                    );
                });
            }
        }
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        dispatch({
            payload: { saveSearchKey },
            type: SAVE_SEARCH_CHECK_REQUEST,
        });

        const response = await api.postIsSavedSearch({ authToken, deployment, ssParams });
        dispatch({
            payload: { savedSearchId: response.payload, saveSearchKey },
            type: SAVE_SEARCH_CHECK_SUCCESS,
        });
    } catch (error) {
        const saveSearchKey = createSaveSearchKeyBySearchParams(ssParams);
        dispatch({
            payload: { saveSearchKey },
            type: SAVE_SEARCH_CHECK_FAIL,
        });
    }
};
